import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CpuChipIcon } from '@heroicons/react/24/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { completeStep, selectJourneyContext, selectSnowflakeAccountId } from '../reducers/onboardingSlice';
import { signupApi, SignupStatus } from 'features/frictionless-signup/services/signupApi';
import OnboardingLayout from './OnboardingLayout';
import DataOpsIcon from 'components/DataOpsIcon';
import RedirectCountdown from './RedirectCountdown';
import RunnerStatusDisplay from 'components/RunnerStatusDisplay';

/**
 * RunnerProvisionStep Component
 *
 * Runner provisioning step with enhanced design.
 * Automatically provisions a runner for the user and displays progress with
 * professional animations and status messages.
 *
 * Features:
 * - Auto-provisioning on mount
 * - Three states: provisioning, success, error
 * - Framer Motion animations for smooth transitions
 * - Journey context awareness for personalized messaging
 * - Automatic redirect after successful provisioning
 * - Error handling with retry functionality
 *
 * State Flow:
 * 1. 'provisioning' - Auto-triggers provisioning on mount, shows animated loading spinner
 * 2. 'success' - Shows success message for 2 seconds, then completes step
 * 3. 'error' - Shows error message with retry button
 *
 * @component
 * @example
 * ```tsx
 * <RunnerProvisionStep />
 * ```
 *
 * @returns Runner provisioning step with auto-provisioning and animated status display
 */
const RunnerProvisionStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const journeyContext = useAppSelector(selectJourneyContext);
  const snowflakeAccountId = useAppSelector(selectSnowflakeAccountId);
  const [status, setStatus] = useState<SignupStatus>(SignupStatus.RUNNER_PROVISIONING);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);

  /**
   * Triggers runner provisioning (fire-and-forget)
   * We don't show errors from this endpoint - we rely on status polling instead
   * The backend uses the authenticated user's context to determine which signup to provision.
   */
  const triggerProvisionRunner = async (): Promise<void> => {
    try {
      await signupApi.provisionRunner();
      console.log('Runner provisioning triggered successfully');
    } catch (err: any) {
      // Ignore errors from provision endpoint - we'll rely on status polling
      console.log('Provision runner endpoint returned error (ignored):', err.message);
    }
  };

  /**
   * Poll the status endpoint to check provisioning progress
   */
  const pollStatus = async (): Promise<void> => {
    try {
      const statusResponse = await signupApi.getCurrentUserSignupStatus();
      if (statusResponse !== null) {
        console.log('[RunnerProvisionStep] Current status:', statusResponse.current_step);
        setStatus(statusResponse.current_step);

        // If provisioning is complete or failed, stop polling and transition
        // Accept both RUNNER_PROVISIONED and SIGNUP_COMPLETED as success states
        if (
          statusResponse.current_step === SignupStatus.RUNNER_PROVISIONED ||
          statusResponse.current_step === SignupStatus.SIGNUP_COMPLETED
        ) {
          console.log('[RunnerProvisionStep] Runner provisioned successfully, starting countdown');
          if (pollingInterval !== null) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          if (pollingTimeout !== null) {
            clearTimeout(pollingTimeout);
            setPollingTimeout(null);
          }
          // Wait 3 seconds then complete onboarding
          setTimeout(() => {
            console.log('[RunnerProvisionStep] Redirecting to dashboard');
            dispatch(completeStep('runner_provision'));
          }, 3000);
        } else if (statusResponse.current_step === SignupStatus.RUNNER_PROVISIONING_FAILED) {
          console.log('[RunnerProvisionStep] Runner provisioning failed');
          if (pollingInterval !== null) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          if (pollingTimeout !== null) {
            clearTimeout(pollingTimeout);
            setPollingTimeout(null);
          }
        }
      }
    } catch (err: any) {
      console.error('[RunnerProvisionStep] Error polling signup status:', err);
    }
  };

  /**
   * Initialize provisioning and status polling
   */
  useEffect(() => {
    // Trigger provisioning (don't wait for response)
    // Backend uses authenticated user's context to determine which signup to provision
    console.log('[RunnerProvisionStep] Triggering runner provisioning');
    void triggerProvisionRunner();

    // Start polling status every 2 seconds
    const interval = setInterval(() => {
      void pollStatus();
    }, 2000);
    setPollingInterval(interval);

    // Set timeout to stop polling after 5 minutes (300 seconds)
    // Runner provisioning can take a while
    const timeout = setTimeout(() => {
      console.log('[RunnerProvisionStep] Polling timeout reached (5 minutes) - stopping polling');
      if (interval !== null) {
        clearInterval(interval);
        setPollingInterval(null);
      }
    }, 300000);
    setPollingTimeout(timeout);

    // Poll immediately as well
    console.log('[RunnerProvisionStep] Starting status polling');
    void pollStatus();

    // Cleanup on unmount
    return () => {
      if (interval !== null) {
        clearInterval(interval);
      }
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [dispatch, snowflakeAccountId]);

  /**
   * Handles retry action - restart provisioning and polling
   */
  const handleRetry = (): void => {
    // Reset status and restart
    console.log('[RunnerProvisionStep] Retrying runner provisioning');
    setStatus(SignupStatus.RUNNER_PROVISIONING);
    void triggerProvisionRunner();

    // Start polling again
    const interval = setInterval(() => {
      void pollStatus();
    }, 2000);
    setPollingInterval(interval);

    // Set timeout to stop polling after 5 minutes (300 seconds)
    const timeout = setTimeout(() => {
      console.log('[RunnerProvisionStep] Retry polling timeout reached (5 minutes) - stopping polling');
      if (interval !== null) {
        clearInterval(interval);
        setPollingInterval(null);
      }
    }, 300000);
    setPollingTimeout(timeout);

    void pollStatus();
  };

  /**
   * Handles returning to dashboard
   */
  const handleReturnToDashboard = (): void => {
    dispatch(completeStep('runner_provision'));
  };

  return (
    <OnboardingLayout currentStep="runner_provision">
      <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-lg bg-white px-4 py-6 shadow-sm sm:px-6 lg:px-8"
        >
          {/* Header Section */}
          <div className="py-12 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 border-b border-gray-200">
            <div className="shrink-0">
              <div
                className="relative h-16 w-16 flex items-center justify-center rounded-full bg-dataops-primary-blue/10"
                role="img"
                aria-label="DataOps Runner"
              >
                <DataOpsIcon
                  size="h-7"
                  className="z-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.7, delay: 0, type: 'spring', bounce: 0.7 }}
                />
                <CpuChipIcon className="absolute top-0 left-0 h-16 text-white" aria-hidden="true" />
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-lg  text-dataops-dark">Get your DataOps Runner</h2>
              <p className="mt-1 max-w-2xl text-sm text-secondary-text">
                {journeyContext != null
                  ? `To use ${journeyContext.app_name} you will need a runner compute that executes your DataOps workflows.`
                  : 'To use DataOps.live you will need a runner compute that executes your DataOps workflows.'}
              </p>
            </div>
          </div>

          {/* Status Section */}
          <div className="mt-8 sm:mt-12 flex flex-col items-center px-4">
            <div className="w-full max-w-2xl">
              <div className="pb-12">
                <h3 className="text-base text-dataops-dark mb-6 text-center sm:text-left">Status</h3>

                {/* Use RunnerStatusDisplay component */}
                <RunnerStatusDisplay status={status} />

                {/* Success countdown - shown when runner is ready */}
                {(status === SignupStatus.RUNNER_PROVISIONED || status === SignupStatus.SIGNUP_COMPLETED) && (
                  <div className="text-center mt-4">
                    <RedirectCountdown seconds={3} message="Redirecting to dashboard in" />
                  </div>
                )}

                {/* Retry button - shown on error */}
                {status === SignupStatus.RUNNER_PROVISIONING_FAILED && (
                  <div className="text-center mt-4">
                    <motion.button
                      onClick={handleRetry}
                      className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-sm rounded-md text-white bg-dataops-primary-blue hover:bg-hover-primary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue transition-all duration-150"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      aria-label="Retry runner provisioning"
                    >
                      Try again
                    </motion.button>
                  </div>
                )}

                {/* Skip to dashboard button - always shown */}
                <div className="text-center mt-4">
                  <motion.button
                    onClick={handleReturnToDashboard}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm rounded-md text-primary-text bg-white hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue transition-all duration-150"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    aria-label="Skip to dashboard"
                  >
                    Skip to dashboard
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </OnboardingLayout>
  );
};

export default RunnerProvisionStep;
